import { inject, Injectable } from '@angular/core';
import {
  type BatchMutationVariables,
  invalidateObject,
  isNotNull,
  typeOrNull,
} from '@reconcept-platform/shared/data-access';
import {
  type CreateQualityInstrumentInput,
  type CreateQualityInstrumentMutation,
  type CreateQualityInstrumentMutationVariables,
  type DestroyQualityInstrumentMutation,
  type DestroyQualityInstrumentMutationVariables,
  type QualityInstrumentDetailFragment,
  type QualityInstrumentsForDashboardQuery,
  type QualityInstrumentsForDashboardQueryVariables,
  type QualityInstrumentsForWorkspaceQuery,
  type QualityInstrumentsForWorkspaceQueryVariables,
  type UpdateQualityInstrumentInput,
  type UpdateQualityInstrumentMutation,
  type UpdateQualityInstrumentMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { orderBy } from 'lodash-es';
import { type Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { qualityInstrumentDetailFragment } from './quality-instrument.fragments';

@Injectable({
  providedIn: 'root',
})
export class QualityInstrumentApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchQualityInstrumentsForWorkspace(workspaceId: string): Observable<QualityInstrumentDetailFragment[]> {
    return this.workspaceGraphQLService
      .watchQuery<QualityInstrumentsForWorkspaceQuery, QualityInstrumentsForWorkspaceQueryVariables>({
        query: gql`
          query QualityInstrumentsForWorkspace($id: ID!) {
            node(id: $id) {
              ... on Workspace {
                id
                dashboard {
                  id
                  qualityInstruments {
                    nodes {
                      ...QualityInstrumentDetail
                    }
                  }
                }
              }
            }
          }
          ${qualityInstrumentDetailFragment}
        `,
        variables: {
          id: workspaceId,
        },
      })
      .pipe(
        map((result) =>
          orderBy(
            typeOrNull(result.data.node, 'Workspace')?.dashboard?.qualityInstruments?.nodes?.filter(isNotNull),
            'order',
          ),
        ),
      );
  }

  watchQualityInstrumentsForDashboard(dashboardId: string): Observable<QualityInstrumentDetailFragment[]> {
    return this.workspaceGraphQLService
      .watchQuery<QualityInstrumentsForDashboardQuery, QualityInstrumentsForDashboardQueryVariables>({
        query: gql`
          query QualityInstrumentsForDashboard($id: ID!) {
            node(id: $id) {
              ... on Dashboard {
                id
                title
                qualityInstruments {
                  nodes {
                    ...QualityInstrumentDetail
                  }
                }
              }
            }
          }
          ${qualityInstrumentDetailFragment}
        `,
        variables: {
          id: dashboardId,
        },
      })
      .pipe(
        map((result) =>
          orderBy(typeOrNull(result.data.node, 'Dashboard')?.qualityInstruments?.nodes?.filter(isNotNull), 'order'),
        ),
      );
  }

  createQualityInstrument(createQualityInstrumentInput: CreateQualityInstrumentInput) {
    return this.workspaceGraphQLService
      .mutateV2<CreateQualityInstrumentMutation, CreateQualityInstrumentMutationVariables>({
        mutation: gql`
          mutation createQualityInstrument($input: CreateQualityInstrumentInput!) {
            createQualityInstrument(input: $input) {
              qualityInstrument {
                ...QualityInstrumentDetail
              }

              errors {
                message
                path
              }
            }
          }
          ${qualityInstrumentDetailFragment}
        `,
        variables: {
          input: createQualityInstrumentInput,
        },
        update: (cache) => {
          invalidateObject('Dashboard', createQualityInstrumentInput.dashboardId)(cache);
        },
      })
      .pipe(map((result) => result.data?.createQualityInstrument?.qualityInstrument));
  }

  updateQualityInstrument(updateQualityInstrumentInput: UpdateQualityInstrumentInput) {
    return this.workspaceGraphQLService
      .mutateV2<UpdateQualityInstrumentMutation, UpdateQualityInstrumentMutationVariables>({
        mutation: gql`
          mutation updateQualityInstrument($input: UpdateQualityInstrumentInput!) {
            updateQualityInstrument(input: $input) {
              qualityInstrument {
                ...QualityInstrumentDetail
              }

              errors {
                message
                path
              }
            }
          }
          ${qualityInstrumentDetailFragment}
        `,
        variables: {
          input: updateQualityInstrumentInput,
        },
      })
      .pipe(map((result) => result.data?.updateQualityInstrument?.qualityInstrument));
  }

  destroyQualityInstrument(dashboardId: string, qualityInstrumentId: string) {
    return this.workspaceGraphQLService
      .mutateV2<DestroyQualityInstrumentMutation, DestroyQualityInstrumentMutationVariables>({
        mutation: gql`
          mutation destroyQualityInstrument($input: DestroyQualityInstrumentInput!) {
            destroyQualityInstrument(input: $input) {
              errors {
                message
                path
              }
            }
          }
        `,
        variables: {
          input: {
            qualityInstrumentId: qualityInstrumentId,
          },
        },
        update: (cache) => {
          invalidateObject('Dashboard', dashboardId)(cache);
        },
      })
      .pipe(map((result) => !result.data?.destroyQualityInstrument?.errors.length));
  }

  reorderQualityInstruments(qualityInstruments: QualityInstrumentDetailFragment[]): Observable<boolean> {
    const updateCalls = qualityInstruments.map((qualityInstrument, index) => {
      return {
        qualityInstrumentId: qualityInstrument.id as string,
        order: index,
      };
    });

    const mutations: string[] = [];
    const variables: { [variableKey: string]: BatchMutationVariables }[] = [];
    const batchUpdateQualityInstruments = `
     updateQualityInstrument(input: $input) {
        qualityInstrument {
          id
          order
        }

        errors {
          message
          path
        }
      }`;

    updateCalls.forEach((input: UpdateQualityInstrumentInput) => {
      mutations.push(batchUpdateQualityInstruments);
      variables.push({
        input: {
          type: 'UpdateQualityInstrumentInput!',
          value: input,
        },
      });
    });

    return this.workspaceGraphQLService
      .mutateBatch({
        mutations,
        variables,
      })
      .pipe(
        map(() => true),
        catchError(() => of(false)),
      );
  }
}
